/** @forge-header
 * @id          clientLog
 * @role        Fire-and-forget client→relay error reporter
 * @pattern     pure-fn
 * @tier        T1
 * @purity      api
 * @units       n/a
 * @upstream    none
 * @downstream  ErrorBoundary | main.tsx (global handlers)
 * @exports     sendClientLog:fn
 */

type LogLevel = 'error' | 'warn' | 'info';

interface ClientLogPayload {
  level: LogLevel;
  message: string;
  source?: string;
  stack?: string;
  context?: Record<string, unknown>;
}

// ── Rate limiter (max 10 errors per 60s window) ────────────────────
const RATE_LIMIT = 10;
const RATE_WINDOW_MS = 60_000;
const timestamps: number[] = [];

function isRateLimited(): boolean {
  const now = Date.now();
  // Prune old entries
  while (timestamps.length > 0 && now - timestamps[0] > RATE_WINDOW_MS) {
    timestamps.shift();
  }
  if (timestamps.length >= RATE_LIMIT) return true;
  timestamps.push(now);
  return false;
}

// ── Relay URL resolution (same logic as relayWs.ts) ────────────────
function getRelayBaseUrl(): string {
  const env = (import.meta.env.VITE_RELAY_URL as string | undefined)?.trim();
  if (env && env.length > 0) return env;
  if (import.meta.env.DEV) return 'http://localhost:8787';
  return `${window.location.protocol}//${window.location.host}`;
}

// ── Build metadata ─────────────────────────────────────────────────
const BUILD_COMMIT = (import.meta.env.VITE_BUILD_COMMIT as string) || 'dev';

// ── Session ID (stable per page load) ──────────────────────────────
const SESSION_ID = crypto.randomUUID();

/**
 * Send an error log to the relay server. Fire-and-forget — never throws.
 * Uses sendBeacon (survives page unload) with fetch fallback.
 */
export function sendClientLog(
  level: LogLevel,
  message: string,
  opts?: { source?: string; stack?: string; context?: Record<string, unknown> }
): void {
  if (isRateLimited()) return;

  const payload: ClientLogPayload & { ts: string; url: string; build: string; sessionId: string } =
    {
      level,
      message,
      source: opts?.source,
      stack: opts?.stack,
      context: opts?.context,
      ts: new Date().toISOString(),
      url: typeof window !== 'undefined' ? (window.location?.href ?? '') : '',
      build: BUILD_COMMIT,
      sessionId: SESSION_ID,
    };

  const url = `${getRelayBaseUrl()}/relay/client-log`;
  const body = JSON.stringify(payload);

  try {
    // sendBeacon is non-blocking and survives page unload
    const sent = navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    if (!sent) {
      // Fallback to fetch (fire-and-forget)
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {
        // Silently swallow — we can't log a logging failure
      });
    }
  } catch {
    // Silently swallow
  }
}
